import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";

import { api } from "../lib/api";
import SessionCard from "../components/SessionCard";
import { Input } from "../components/ui/input";

const groupByDay = (sessions) => {
    const groups = {};
    for (const s of sessions) {
        const d = new Date(s.created_at);
        const key = d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
        if (!groups[key]) groups[key] = [];
        groups[key].push(s);
    }
    return groups;
};

export default function History() {
    const [sessions, setSessions] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/sessions")
            .then(({ data }) => setSessions(data))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return sessions;
        return sessions.filter((x) => `${x.prompt_title} ${x.prompt_category} ${x.transcript}`.toLowerCase().includes(s));
    }, [sessions, q]);

    const groups = groupByDay(filtered);

    return (
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-8" data-testid="history-page">
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400">Every rep counts</div>
                <h1 className="font-serif text-4xl sm:text-5xl tracking-tight font-light mt-2">Your history</h1>
            </motion.div>

            <div className="mt-6 relative">
                <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={1.5} />
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search your sessions…"
                    className="pl-10 rounded-full h-11"
                    data-testid="history-search"
                />
            </div>

            <div className="mt-8 space-y-8">
                {loading && <div className="text-stone-500 italic font-serif text-xl">Pulling up your work…</div>}
                {!loading && filtered.length === 0 && (
                    <div className="rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-12 text-center" data-testid="history-empty">
                        <div className="font-serif text-3xl tracking-tight font-light italic text-stone-500">No reps yet.</div>
                        <p className="text-sm text-stone-500 mt-2">Head back to Practice and try a prompt to see it appear here.</p>
                    </div>
                )}
                {Object.entries(groups).map(([day, items]) => (
                    <div key={day}>
                        <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400 mb-3">{day}</div>
                        <div className="grid gap-3">
                            {items.map((s) => (
                                <SessionCard key={s.id} session={s} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
